import React, {useMemo, useRef} from 'react';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
  TooltipItem,
} from 'chart.js';
import {Chart} from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import {de} from 'date-fns/locale';
import {Timeline} from '@mui/icons-material';
import {ItemHistory, Timeframe} from './ItemAnalytics';
import {moneyFromMinor} from '../../../utils/UnitConversion.utils';

ChartJS.register(
    CategoryScale,
    LinearScale,
    TimeScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

interface Props {
  data: ItemHistory[];
  timeframe: Timeframe;
}

const ItemHistoryChart: React.FC<Props> = ({ data, timeframe }) => {
  // Using any due to complex ChartJS types with react-chartjs-2
  const chartRef = useRef<any>(null);

  // Define the primary blue color from your theme
  const themeBlue = '#3f51b5';
  const themeBlueTransparent = 'rgba(63, 81, 181, 0.5)';

  const bounds = useMemo(() => {
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    const start = new Date();

    if (timeframe === '1m') start.setMonth(now.getMonth() - 1);
    else if (timeframe === '3m') start.setMonth(now.getMonth() - 3);
    else if (timeframe === '1y') start.setFullYear(now.getFullYear() - 1);
    else return { min: undefined, max: undefined };

    start.setHours(0, 0, 0, 0);
    return { min: start.getTime(), max: now.getTime() };
  }, [timeframe]);

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const sorted = [...data].sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
    if (timeframe === 'all' || !bounds.min) return sorted;
    return sorted.filter(item => new Date(item.day).getTime() >= bounds.min!);
  }, [data, timeframe, bounds]);

  const maxSales = useMemo(() => {
    if (filteredData.length === 0) return 1;
    return Math.max(...filteredData.map(d => d.count), 1);
  }, [filteredData]);

  const chartData: ChartData<any> = useMemo(() => ({
    datasets: [
      {
        type: 'line' as const,
        label: 'Average Price',
        data: filteredData.map(d => ({ x: new Date(d.day).getTime(), y: d.avgPriceMinor })),
        borderColor: themeBlue,
        borderWidth: 4,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: themeBlue,
        pointHoverBorderColor: '#1a1d24',
        pointHoverBorderWidth: 3,
        tension: 0.2,
        yAxisID: 'y',
        order: 1,
        spanGaps: true
      },
      {
        type: 'bar' as const,
        label: 'Sales',
        data: filteredData.map(d => ({ x: new Date(d.day).getTime(), y: d.count })),
        backgroundColor: themeBlueTransparent,
        borderRadius: 0,
        yAxisID: 'y1',
        order: 2,
        barPercentage: 1.0,
        categoryPercentage: 1.0,
      },
    ],
  }), [filteredData, themeBlue, themeBlueTransparent]);

  const options: ChartOptions<any> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 300, easing: 'linear' },
    hover: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: '#1c1f26',
        titleColor: '#9ca3af',
        bodyColor: '#fff',
        borderColor: '#374151',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 12,
        displayColors: false,
        callbacks: {
          label: (context: TooltipItem<any>): string => {
            const value = context.parsed.y ?? 0;
            if (context.datasetIndex === 0) return `Avg Price: ${moneyFromMinor(value).toFixed(2)} €`;
            return `Sales: ${value}`;
          },
          title: (items: TooltipItem<any>[]): string => {
            if (!items.length) return '';
            return new Date(items[0].parsed.x).toLocaleDateString('de-DE', {
              day: '2-digit', month: '2-digit', year: 'numeric'
            });
          }
        }
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: timeframe === '1m' ? 'day' : timeframe === '3m' ? 'week' : 'month',
          displayFormats: { day: 'dd.MM', week: 'dd.MM', month: 'MMM yy' }
        },
        adapters: { date: { locale: de } },
        min: bounds.min,
        max: bounds.max,
        grid: { display: false },
        ticks: { color: '#4b5563', font: { size: 10 }, maxRotation: 0, autoSkip: true }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        grid: { color: '#1f2937' },
        ticks: {
          color: '#4b5563',
          font: { size: 10 },
          callback: (val: string | number) => `${moneyFromMinor(Number(val)).toFixed(0)}€`
        }
      },
      y1: {
        type: 'linear',
        display: false,
        position: 'right',
        min: 0,
        suggestedMax: maxSales * 6, // Compresses bars to the bottom
      },
    },
  };

  if (filteredData.length === 0) {
    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-gray-600 rounded-xl">
          <Timeline sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
          <span className="text-sm font-medium">No price history found</span>
        </div>
    );
  }

  return (
      <div className="w-full h-full outline-none select-none">
        <Chart ref={chartRef} type="bar" data={chartData} options={options} />
      </div>
  );
};

export default ItemHistoryChart;